/**
 * Assessment tools shared by the Appointment dialog and the
 * 'send_assessment' note-template field (Jun 2026).
 *
 *  - RecentAssessments: client's completed assessments in the last 30
 *    days (name, score, date); falls back to the most recent one ever.
 *  - AssessmentQuickSend: pick an existing assessment template and send
 *    it to the client (email + portal) via POST /client-portal/
 *    send-assessment — the exact same endpoint the client Assessment
 *    tab uses, so it lands in that tab and creates the awaiting-client-
 *    signature task. Optional + dismissable everywhere it appears.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClipboardList, Send, X, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecentAssessments = ({ clientId }) => {
  const [scores, setScores] = useState(null);
  useEffect(() => {
    if (!clientId) { setScores(null); return; }
    let live = true;
    axios.get(`${API}/clients/${clientId}/assessment-scores?limit=50`)
      .then((r) => { if (live) setScores(Array.isArray(r.data) ? r.data : (r.data?.scores || [])); })
      .catch(() => { if (live) setScores([]); });
    return () => { live = false; };
  }, [clientId]);

  if (!clientId || scores === null) return null;
  const fmt = (d) => {
    try {
      if (typeof d === 'string') {
        const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m && !d.includes('T')) {
          // Date-only — display verbatim, never shift across timezones
          return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
        }
        let iso = d;
        // Backend stores naive UTC timestamps — mark as UTC so the
        // browser converts to the user's local date correctly.
        if (d.includes('T') && !/([Zz]|[+-]\d{2}:?\d{2})$/.test(d)) iso = `${d}Z`;
        return new Date(iso).toLocaleDateString();
      }
      return new Date(d).toLocaleDateString();
    } catch { return d; }
  };
  const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
  const getDate = (s) => s.assessment_date || s.created_at;
  const recent = scores.filter((s) => { const t = Date.parse(getDate(s)); return t && t >= cutoff; });

  if (recent.length === 0) {
    const last = scores[0];
    return (
      <p className="text-xs text-gray-500 italic" data-testid="assessments-none-recent">
        {last
          ? `No assessments in the last 30 days — most recent: ${last.template_name || last.assessment_name || 'Assessment'} on ${fmt(getDate(last))}`
          : 'No completed assessments on file for this client'}
      </p>
    );
  }
  return (
    <ul className="space-y-1" data-testid="assessments-recent-list">
      {recent.map((s) => (
        <li key={s.id} className="text-xs flex items-center gap-2">
          <ClipboardList className="h-3 w-3 text-purple-600 shrink-0" />
          <span className="font-medium">{s.template_name || s.assessment_name || 'Assessment'}</span>
          <span className="text-purple-700">Score: {s.total_score ?? s.score ?? '—'}{s.severity_label ? ` (${s.severity_label})` : ''}</span>
          <span className="text-gray-500">{fmt(getDate(s))}</span>
          {s.completed_by === 'client' ? (
            <span className="text-[10px] font-medium bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`assessment-self-admin-${s.id}`}>
              Self-Administered
            </span>
          ) : (s.completed_by_name ? <span className="text-gray-500">by {s.completed_by_name}</span> : null)}
          {s.completed_by === 'client' ? (
            <span className="text-[10px] font-medium bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`assessment-self-admin-${s.id}`}>
              Self-Administered
            </span>
          ) : (s.completed_by_name ? <span className="text-gray-500">by {s.completed_by_name}</span> : null)}
        </li>
      ))}
    </ul>
  );
};

export const AssessmentQuickSend = ({ clientId, onSent, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || templates.length) return;
    axios.get(`${API}/assessment-templates`)
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Could not load assessment templates'));
  }, [open, templates.length]);

  const send = async () => {
    if (!templateId || !clientId) return;
    setSending(true);
    try {
      await axios.post(`${API}/client-portal/send-assessment`, { client_id: clientId, template_id: templateId });
      toast.success('Assessment sent — emailed to the client and added to their portal');
      setOpen(false);
      setTemplateId('');
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send assessment');
    } finally {
      setSending(false);
    }
  };

  if (!clientId) return null;
  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline"
        className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
        onClick={() => setOpen(true)} data-testid="assessment-quicksend-open-btn">
        <Send className="h-3 w-3 mr-1" /> Send Assessment
      </Button>
    );
  }
  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'flex-wrap'}`} data-testid="assessment-quicksend-panel">
      <Select value={templateId} onValueChange={setTemplateId}>
        <SelectTrigger className="h-8 text-xs w-64" data-testid="assessment-quicksend-select">
          <SelectValue placeholder="Choose an assessment…" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" size="sm" className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
        disabled={!templateId || sending} onClick={send} data-testid="assessment-quicksend-send-btn">
        {sending ? 'Sending…' : 'Send to Client'}
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0"
        onClick={() => setOpen(false)} title="Dismiss" data-testid="assessment-quicksend-dismiss-btn">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Combined section for the appointment dialog.
export const AppointmentAssessmentsSection = ({ clientId }) => {
  const [expanded, setExpanded] = useState(false);
  const [recentCount, setRecentCount] = useState(null);

  useEffect(() => {
    setExpanded(false);
    if (!clientId) { setRecentCount(null); return; }
    let live = true;
    axios.get(`${API}/clients/${clientId}/assessment-scores?limit=50`)
      .then((r) => {
        if (!live) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data?.scores || []);
        const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
        const n = rows.filter((s) => {
          const d = s.completed_at || s.created_at || s.date;
          const t = d ? new Date(typeof d === 'string' && d.includes('T') && !/([Zz]|[+-]\d{2}:?\d{2})$/.test(d) ? `${d}Z` : d).getTime() : NaN;
          return Number.isFinite(t) && t >= cutoff;
        }).length;
        setRecentCount(n);
      })
      .catch(() => { if (live) setRecentCount(null); });
    return () => { live = false; };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/50 overflow-hidden"
      data-testid="appointment-assessments-section">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full p-3 flex items-center justify-between hover:bg-purple-100/60 transition-colors"
        data-testid="appointment-assessments-toggle"
      >
        <span className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
          <ClipboardList className="h-4 w-4" /> Assessments
          {recentCount === 0 && (
            <span className="text-[11px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
              none in last 30 days
            </span>
          )}
          {recentCount > 0 && (
            <span className="text-[11px] font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {recentCount} in last 30 days
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-purple-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-purple-200 pt-2">
          <div className="flex items-center justify-end">
            <AssessmentQuickSend clientId={clientId} />
          </div>
          <RecentAssessments clientId={clientId} />
        </div>
      )}
    </div>
  );
};

export default AssessmentQuickSend;
